/**
 * Subscription Service
 * Handles subscription and payment related API calls
 */

import api from './apiClient';

// ============================================
// TYPES
// ============================================

export interface Product {
  id: string;
  sku: string;
  name: string;
  description: string;
  names?: Record<string, string>;
  descriptions?: Record<string, string>;
  productType: 'subscription' | 'one_time' | 'bundle' | 'credits' | 'tip';
  tier: 'free' | 'premium' | 'yearly' | 'lifetime';
  priceUsd: number;
  currency: string;
  stripePriceId?: string;
  features?: string[];
  billingPeriod?: string;
  trialDays?: number;
}

export interface CheckoutResponse {
  url: string;
  sessionId: string;
}

export interface PortalResponse {
  url: string;
}

export interface MySubscription {
  id: string;
  status: string;
  platform: string;
  startedAt: string;
  expiresAt: string | null;
  trialEndsAt: string | null;
  cancelledAt: string | null;
  billingPeriod: string | null;
  productName: string | null;
  priceUsd: number | null;
  stripePriceId: string | null;
}

// ============================================
// API FUNCTIONS
// ============================================

/**
 * Get current user's active subscription details
 */
export const getMySubscription = async (): Promise<MySubscription | null> => {
  const response = await api.get('/payments/my-subscription');
  return response.data.subscription;
};

/**
 * Get all available products/plans
 */
export const getProducts = async (): Promise<Product[]> => {
  const response = await api.get('/products');
  return response.data.data;
};

/**
 * Get products by type (subscription, one_time, etc.)
 */
export const getProductsByType = async (type: string): Promise<Product[]> => {
  const response = await api.get(`/products?type=${type}`);
  return response.data.data;
};

/**
 * Create a subscription checkout session
 * Redirects user to Stripe Checkout
 * Accepts locale + period for regional pricing resolution
 */
export const createSubscriptionCheckout = async (locale: string, period: 'monthly' | 'yearly'): Promise<CheckoutResponse> => {
  const response = await api.post('/payments/checkout-subscription', { locale, period });
  return response.data;
};

/**
 * Create a customer portal session
 * Allows user to manage their subscription
 */
export const createPortalSession = async (): Promise<PortalResponse> => {
  const response = await api.post('/payments/portal');
  return response.data;
};

/**
 * Create a course purchase checkout session
 */
export const createCourseCheckout = async (courseId: string): Promise<{ clientSecret: string; transactionId: string }> => {
  const response = await api.post('/payments/checkout', { courseId });
  return response.data;
};

/**
 * Redirect to Stripe Checkout with regional pricing
 */
export const redirectToCheckout = async (locale: string, period: 'monthly' | 'yearly'): Promise<void> => {
  const { url } = await createSubscriptionCheckout(locale, period);
  window.location.href = url;
};

/**
 * Redirect to Customer Portal
 */
export const redirectToPortal = async (): Promise<void> => {
  const { url } = await createPortalSession();
  window.location.href = url;
};

/**
 * Switch subscription plan (monthly ↔ yearly) with proration
 */
export const switchPlan = async (locale: string, period: 'monthly' | 'yearly'): Promise<{ success: boolean }> => {
  const response = await api.post('/payments/switch-plan', { locale, period });
  return response.data;
};

/**
 * Submit cancellation reason/feedback
 */
export const submitCancellationReason = async (reason: string, feedback?: string): Promise<{ success: boolean }> => {
  const response = await api.post('/payments/cancellation-reason', { reason, feedback });
  return response.data;
};

/**
 * Check if user has active subscription
 */
export const isSubscriptionActive = (status: string): boolean => {
  return status === 'active' || status === 'trialing';
};

/**
 * Join purchase waitlist (Stripe coming-soon fallback)
 */
export const joinWaitlist = async (
  email: string,
  locale: string,
  source: string,
): Promise<{ success: boolean; alreadyRegistered: boolean }> => {
  const response = await api.post('/payments/waitlist', { email, locale, source });
  return response.data;
};
